// src/pages/onboarding/patient/steps/StepProfileComplete.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { markSettingsWelcomePending } from "@/lib/settingsWelcomeState.js";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";
import { clearProgress } from "@/lib/persistStep";
import { writeLastAuthedPath } from "@/lib/authedPathCache";
import { writeLastRole } from "@/lib/roleCache";
import { advanceStagingFirstUse } from "@/services/stagingAccessService.js";

const PATIENT_PROGRESS_KEY = "onb_patient_v2";

function withTimeout(promise, ms = 10000, label = "Request") {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);
}

export default function StepProfileComplete({ step = 16, total = 16, onBack, onContinue }) {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Resume behavior: if onboarding is already complete, go straight to Settings
  // for the first-time orientation handoff. Later logins route completed users Home.
  useEffect(() => {
    let alive = true;

    async function guard() {
      try {
        const {
          data: { user },
          error: uErr,
      } = await withTimeout(supabase.auth.getUser(), 7000, "Session");
      if (uErr) throw uErr;
      if (!user || !alive) return;

        const { data, error } = await withTimeout(
          supabase
            .from("profiles")
            .select("onboarding_completed")
            .eq("user_id", user.id)
            .maybeSingle(),
          7000,
          "Onboarding status check"
        );

        if (!alive) return;

        if (error) return;
        if (data?.onboarding_completed) {
          navigate("/settings/patient", { replace: true });
        }
      } catch {
        // silent — we don't block the screen
      }
    }

    guard();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const finishOnboarding = async () => {
    if (saving) return;

    try {
      setSaving(true);
      setErr("");

      const {
        data: { user },
        error: uErr,
      } = await withTimeout(supabase.auth.getUser(), 7000, "Session");
      if (uErr && !canUseOnboardingDraftMode()) throw uErr;
      if (!user) {
        if (canUseOnboardingDraftMode()) {
          navigate("/settings/patient", { replace: true });
          return;
        }
        throw new Error("Session expired");
      }

      if (typeof onContinue === "function") {
        await Promise.resolve(onContinue({ user }));
        await advanceStagingFirstUse("completed").catch(() => {});

        if (typeof refreshProfile === "function") {
          await withTimeout(
            Promise.resolve(refreshProfile()),
            5000,
            "Profile refresh"
          ).catch(() => {});
        }

        await markSettingsWelcomePending(user.id, "patient");
        clearProgress(PATIENT_PROGRESS_KEY);
        writeLastRole("patient", user.id);
        writeLastAuthedPath("/settings/patient", user.id);
        navigate("/settings/patient", {
          replace: true,
          state: { showSettingsWelcome: true },
        });
        return;
      }

      const profilePatch = {
        role: "patient",
        role_selected: true,
        onboarding_completed: true,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await withTimeout(
        supabase
          .from("profiles")
          .upsert({ user_id: user.id, ...profilePatch }, { onConflict: "user_id" })
          .select("user_id,onboarding_completed")
          .single(),
        10000,
        "Final profile save"
      );

      if (error) throw error;
      if (!data?.onboarding_completed) throw new Error("Final save did not persist.");

      await advanceStagingFirstUse("completed").catch(() => {});

      // Keep patient profile verification state aligned, but never block completion.
      await withTimeout(
        supabase
          .from("patient_profiles")
          .update({
            verification_status: "pending",
            updated_at: new Date().toISOString(),
          })
          .eq("user_id", user.id),
        6000,
        "Patient verification status sync"
      ).catch(() => {});

      if (typeof refreshProfile === "function") {
        await withTimeout(
          Promise.resolve(refreshProfile()),
          5000,
          "Profile refresh"
        ).catch(() => {});
      }

      await markSettingsWelcomePending(user.id, "patient");
      clearProgress(PATIENT_PROGRESS_KEY);
      writeLastRole("patient", user.id);
      writeLastAuthedPath("/settings/patient", user.id);
      navigate("/settings/patient", {
        replace: true,
        state: { showSettingsWelcome: true },
      });
    } catch (e) {
      console.error("Final save failed:", e);
      setErr(e?.message || "Final save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={null}
        center
        bodyClassName="flex-1 px-4 !min-h-[calc(100dvh-var(--onb-footer-h)-170px)]"
        footer={
          <div className="w-full max-w-[520px] mx-auto px-4 pointer-events-auto">
            <Button fullWidth size="lg" onClick={finishOnboarding} disabled={saving}>
              <span className="text-title-medium">{saving ? "Finalising..." : "Go to settings"}</span>
            </Button>

            {err ? (
              <p className="mt-3 text-center text-body-small text-[rgb(var(--afrimed-danger))]">
                {err}
              </p>
            ) : null}
          </div>
        }
      >
        <div className="w-full flex flex-col items-center justify-center text-center">
          {/* ✅ True center (middle + centre) */}
          <div className="relative mx-auto w-[200px] h-[200px] flex items-center justify-center">
            <img
              src="/icons/profile-complete.svg"
              alt="Complete"
              className="w-[126px] h-[126px] object-contain select-none"
              draggable="false"
            />
            <span
              className="afrimed-shine absolute inset-0 z-10 pointer-events-none"
              aria-hidden
            />
          </div>

          <h2 className="mt-8 text-center text-headline-small text-on-surface">
            Your profile is complete!
          </h2>
        </div>
      </OnboardingSection>
    </div>
  );
}
